import API_BASE from '../config';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import LordIcon from '../components/LordIcon';
import LORDICONS from '../lordicons';
import Checkbox from '../components/Checkbox';
import CalendarBrandIcon from '../components/CalendarBrandIcon';
import { SHOW_APPLE } from '../config/features';
import { applyUserLanguagePreference, setShiftsDefaultView } from '../utils/userPreferences';
import MarketingHeader from './home/MarketingHeader';
import MarketingFooter from './home/MarketingFooter';
import { useContactLanding } from './home/useContactLanding';
import './home/HomeLanding.css';

type DemoUser = {
  email: string;
  password: string;
  label: string;
};

/** Dev-only DB quick login (real Prisma users from seed.js) */
const DEMO_USERS: Record<'owner' | 'employee' | 'manager' | 'admin', DemoUser[]> = {
  owner: [
    { email: 'owner1@example.com', password: 'owner1pass', label: 'Owner One (owner1)' },
  ],
  employee: [
    { email: 'emp1@example.com', password: 'emppass1', label: 'emp one (emp1)' },
    { email: 'emp2@example.com', password: 'emppass2', label: 'emp two (emploeetwo)' },
    { email: 'emp3@example.com', password: 'emppass3', label: 'emp three (emp3)' },
  ],
  manager: [
    { email: 'manager1@example.com', password: 'managerpass1', label: 'manager one (manager1)' },
    { email: 'manager2@example.com', password: 'managerpass2', label: 'manager two (managertwo)' },
  ],
  admin: [
    { email: 'admin@ompli.local', password: 'adminpass', label: 'Platform Admin (superadmin)' },
  ],
};

const IS_DEV = process.env.NODE_ENV === 'development';

const Login: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  const redirectParam = searchParams.get('redirect');
  const safeRedirect =
    redirectParam && /^\/swap\/[a-zA-Z0-9_-]+$/.test(redirectParam) ? redirectParam : null;
  const lang = language === 'en' ? 'en' : 'cs';
  const v = useContactLanding(lang);
  const [role, setRole] = useState<'employer' | 'employee' | 'manager'>('employer');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'google' | 'microsoft' | 'apple' | null>(null);
  const [openQuickRole, setOpenQuickRole] = useState<'owner' | 'employee' | 'manager' | 'admin' | null>(null);

  useEffect(() => {
    // Skip hard reload during OAuth return - ticket must not be interrupted
    if (searchParams.get('oauth')) return;
    // Only perform hard reload once per session
    if (!sessionStorage.getItem('hardReloaded')) {
      sessionStorage.setItem('hardReloaded', 'true');
      window.location.reload();
    }
  }, [searchParams]);

  useEffect(() => {
    const oauth = searchParams.get('oauth');
    if (oauth === 'error') {
      const reason = searchParams.get('reason') || '';
      toast.error(reason ? `${t('login.oauth.error')}: ${reason}` : t('login.oauth.error'));
      navigate('/login', { replace: true });
      return;
    }
    if (oauth !== 'session') return;
    const ticket = searchParams.get('ticket');
    if (!ticket) {
      toast.error(t('login.oauth.error'));
      navigate('/login', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/auth/oauth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ticket }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.user) {
          throw new Error(data.error || t('login.oauth.error'));
        }
        if (cancelled) return;
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user?.shiftsDefaultView === 'list' || data.user?.shiftsDefaultView === 'calendar') {
          setShiftsDefaultView(data.user.shiftsDefaultView, data.user.id);
        }
        applyUserLanguagePreference(data.user);
        window.dispatchEvent(new Event('userUpdated'));
        toast.success(t('login.oauth.success'));
        navigate(safeRedirect || (data.user?.role === 'SUPER_ADMIN' ? '/admin' : '/dashboard'), {
          replace: true,
        });
      } catch (err: any) {
        if (!cancelled) {
          toast.error(err?.message || t('login.oauth.error'));
          navigate('/login', { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, toast, t, navigate, safeRedirect]);

  const startOAuthLogin = async (provider: 'google' | 'microsoft' | 'apple') => {
    setError(null);
    setOauthLoading(provider);
    try {
      const url =
        provider === 'apple'
          ? `${API_BASE}/api/auth/oauth/apple/start`
          : `${API_BASE}/api/calendar/oauth/${provider}/start?purpose=login`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) {
        throw new Error(data.error || t('login.oauth.error'));
      }
      window.location.href = data.url;
    } catch (err: any) {
      toast.error(err?.message || t('login.oauth.error'));
      setOauthLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(API_BASE + '/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('login.login.failed'));
      } else {
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user?.shiftsDefaultView === 'list' || data.user?.shiftsDefaultView === 'calendar') {
          setShiftsDefaultView(data.user.shiftsDefaultView, data.user.id);
        }
        applyUserLanguagePreference(data.user);
        window.dispatchEvent(new Event('userUpdated'));
        if (inviteCode && data.user?.role !== 'SUPER_ADMIN') {
          try {
            await fetch(API_BASE + '/api/join', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ code: inviteCode }),
            });
          } catch {}
        }
        navigate(safeRedirect || (data.user?.role === 'SUPER_ADMIN' ? '/admin' : '/dashboard'));
      }
    } catch (err) {
      setError(t('login.network.error'));
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (creds: DemoUser) => {
    setError(null);
    setLoading(true);
    setOpenQuickRole(null);
    setEmail(creds.email);
    setPassword(creds.password);
    try {
      const res = await fetch(API_BASE + '/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: creds.email, password: creds.password }),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('login.login.failed'));
      } else {
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user?.shiftsDefaultView === 'list' || data.user?.shiftsDefaultView === 'calendar') {
          setShiftsDefaultView(data.user.shiftsDefaultView, data.user.id);
        }
        applyUserLanguagePreference(data.user);
        window.dispatchEvent(new Event('userUpdated'));
        navigate(safeRedirect || (data.user?.role === 'SUPER_ADMIN' ? '/admin' : '/dashboard'));
      }
    } catch (err) {
      setError(t('login.network.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="home-landing min-h-screen flex flex-col bg-[#F7F8FA]"
      style={{ width: '100%', fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <MarketingHeader
        v={v}
        lang={lang}
        onSetCz={() => setLanguage('cs')}
        onSetEn={() => setLanguage('en')}
      />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 pt-28 md:pt-32">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8 bg-white p-4 rounded-xl shadow-lg"
      >
        <div>
          <h2 className="text-center text-xl font-semibold text-gray-900">{t('login.welcome.back')}</h2>
          <p className="mt-2 text-center text-sm text-gray-500">
            {t('login.sign.in.account')}
          </p>
        </div>

        {/* Role selection */}
        <div className="flex space-x-4">
          <button
            onClick={() => setRole('employer')}
            className={`flex-1 flex items-center justify-center px-4 py-2 rounded-lg border ${
              role === 'employer'
                ? 'border-[#0065F4] bg-[#F1F5FF] text-[#0065F4]'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <LordIcon src={LORDICONS.building} trigger="hover" size={18} color={role === 'employer' ? '#0065F4' : '#6B7280'} hoverColor="#0065F4" target="button" />
            <span className="ml-2">{t('login.employer')}</span>
          </button>
          <button
            onClick={() => setRole('employee')}
            className={`flex-1 flex items-center justify-center px-4 py-2 rounded-lg border ${
              role === 'employee'
                ? 'border-[#0065F4] bg-[#F1F5FF] text-[#0065F4]'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <LordIcon src={LORDICONS.user} trigger="hover" size={18} color={role === 'employee' ? '#0065F4' : '#6B7280'} hoverColor="#0065F4" target="button" />
            <span className="ml-2">{t('login.employee')}</span>
          </button>
          <button
            onClick={() => setRole('manager')}
            className={`flex-1 flex items-center justify-center px-4 py-2 rounded-lg border ${
              role === 'manager'
                ? 'border-[#0065F4] bg-[#F1F5FF] text-[#0065F4]'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <LordIcon src={LORDICONS.users} trigger="hover" size={18} color={role === 'manager' ? '#0065F4' : '#6B7280'} hoverColor="#0065F4" target="button" />
            <span className="ml-2">{t('login.manager')}</span>
          </button>
        </div>

        {error && <div className="text-red-500 text-center">{error}</div>}

        <form className="mt-8 space-y-3" onSubmit={handleSubmit}>
          <div className="space-y-3">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('login.email.address')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('login.password')}
              </label>
              <div className="relative flex items-center mt-1">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 pr-10"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? t('login.hide.password') : t('login.show.password')}
                >
                  <LordIcon src={LORDICONS.eye} trigger="hover" size={16} color="#9CA3AF" state={showPassword ? 'morph-close:0.5' : undefined} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Checkbox id="remember-me" checked={false} onChange={() => {}} label={t('login.remember.me')} />
            </div>

            <div className="text-sm">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="font-medium text-[#0065F4] hover:text-[#3384F6]"
              >
                {t('login.forgot.password')}
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={loading}
            >
              {loading ? t('login.signing.in') : t('login.sign.in')}
            </button>
          </div>
        </form>

        {IS_DEV && (
          <div className="mt-4 space-y-2">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Dev DB quick login</p>
            {([
              { key: 'owner' as const, label: t('login.quick.login.owner') },
              { key: 'employee' as const, label: t('login.quick.login.employee') },
              { key: 'manager' as const, label: t('login.quick.login.manager') },
              { key: 'admin' as const, label: t('login.quick.login.admin') },
            ]).map(({ key, label }) => {
              const isOpen = openQuickRole === key;
              const users = DEMO_USERS[key];
              return (
                <div key={key} className="relative">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => setOpenQuickRole(isOpen ? null : key)}
                    className="w-full flex items-center justify-between py-2 px-4 rounded-md bg-primary-100 text-primary-800 font-medium hover:bg-primary-200 disabled:opacity-50"
                  >
                    <span>{label}</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="mt-1 rounded-md border border-primary-200 bg-white shadow-sm overflow-hidden">
                      {users.map((user) => (
                        <button
                          key={user.email}
                          type="button"
                          disabled={loading}
                          onClick={() => quickLogin(user)}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-800 border-b border-gray-100 last:border-b-0 disabled:opacity-50"
                        >
                          <div className="font-medium">{user.label}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t('login.or.continue.with')}</span>
            </div>
          </div>

          <div className={`mt-6 grid gap-3 ${SHOW_APPLE ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <button
              type="button"
              disabled={loading || oauthLoading !== null}
              onClick={() => startOAuthLogin('google')}
              className="w-full inline-flex justify-center items-center py-2 px-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <CalendarBrandIcon provider="GOOGLE" size={20} />
              <span className="ml-2">{oauthLoading === 'google' ? '…' : 'Google'}</span>
            </button>
            <button
              type="button"
              disabled={loading || oauthLoading !== null}
              onClick={() => startOAuthLogin('microsoft')}
              className="w-full inline-flex justify-center items-center py-2 px-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <CalendarBrandIcon provider="MICROSOFT" size={20} />
              <span className="ml-2">{oauthLoading === 'microsoft' ? '…' : 'Microsoft'}</span>
            </button>
            {SHOW_APPLE && (
              <button
                type="button"
                disabled={loading || oauthLoading !== null}
                onClick={() => startOAuthLogin('apple')}
                className="w-full inline-flex justify-center items-center py-2 px-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <CalendarBrandIcon provider="APPLE" size={20} className="text-gray-900" />
                <span className="ml-2">{oauthLoading === 'apple' ? '…' : 'Apple'}</span>
              </button>
            )}
          </div>
        </div>

        <div className="text-center mt-4">
          <p className="text-sm text-gray-500">
            {t('login.dont.have.account')}{' '}
            <button
              onClick={() => navigate('/register')}
              className="font-medium text-[#0065F4] hover:text-[#3384F6]"
            >
              {t('login.register.here')}
            </button>
          </p>
        </div>
      </motion.div>
      </div>

      <MarketingFooter lang={lang} langLabel={v.langLabel} />
    </div>
  );
};

export default Login;

